import React, { useState } from 'react';
import { Box, Chip, Stack, Typography, Button, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import apiClient from '../services/apiClient';

/**
 * "New words" strip under the latest AI reply. Replaces silent auto-mining:
 * the backend suggests up to 6 words from the reply that aren't in the
 * learner's list yet, and the learner chooses which to save.
 *
 * Props:
 *   words         [{ word, translation, context }]
 *   languageCode  target language
 *   onAdded       () => void   refresh vocabulary after a save
 *   onDismiss     () => void   hide the strip for this reply
 */
export default function WordSuggestions({ words = [], languageCode, onAdded, onDismiss }) {
  const [status, setStatus] = useState({}); // word -> 'saving' | 'added'

  if (!words.length) return null;

  const add = async (item) => {
    if (status[item.word]) return;
    setStatus((s) => ({ ...s, [item.word]: 'saving' }));
    try {
      await apiClient.vocabulary.addVocabularyItem({
        language_code: languageCode,
        word: item.word,
        translation: item.translation,
        context: item.context || '',
        knowledge_level: 1,
      });
      setStatus((s) => ({ ...s, [item.word]: 'added' }));
      if (onAdded) onAdded();
    } catch (e) {
      if (e?.response?.status === 409) {
        setStatus((s) => ({ ...s, [item.word]: 'added' }));
      } else {
        console.error('Add suggested word failed', e);
        setStatus((s) => { const n = { ...s }; delete n[item.word]; return n; });
      }
    }
  };

  const remaining = words.filter((w) => !status[w.word]);
  const addAll = async () => { for (const w of remaining) { await add(w); } };

  return (
    <Box sx={{ mt: 0.5, mb: 1.5, ml: { xs: 0, sm: 6 }, px: 1.5, py: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, flexGrow: 1 }}>
          New words in this reply
        </Typography>
        {remaining.length > 1 && (
          <Button size="small" onClick={addAll} sx={{ textTransform: 'none', py: 0 }}>Add all</Button>
        )}
        {onDismiss && (
          <Tooltip title="Hide">
            <IconButton size="small" onClick={onDismiss} aria-label="Hide word suggestions"><CloseIcon fontSize="inherit" /></IconButton>
          </Tooltip>
        )}
      </Stack>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
        {words.map((item) => {
          const st = status[item.word];
          return (
            <Tooltip key={item.word} title={item.context || ''}>
              <Chip
                size="small"
                variant={st === 'added' ? 'filled' : 'outlined'}
                color={st === 'added' ? 'success' : 'default'}
                icon={st === 'added' ? <CheckIcon /> : <AddIcon />}
                label={`${item.word} · ${item.translation}`}
                onClick={() => add(item)}
                disabled={st === 'saving'}
              />
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
}
